import { addBreadCrumbs, setCurrentRoute } from "@/features/currentRouteSlice";
import { setTitle } from "@/helpers/window.helpers";
import type { DispatchFn } from "@/types/data";
import type { Breadcrumb } from "@/types/view";
import { getUrlFromPath } from "./router.helpers";

const routePropertyName = "handleRouteChange";
const breadcrumbPropertyName = "handleBreadcrumbChange";

// TODO: known issue - back navigates the iframe only, then 2nd back navigates
// the main window history back. Results in a "no op" on the first back.

// These paths load a ThingBoard iframe that updates our route
// via `handleExternalRouteChange`.
// Note: only dashboards have a corresponding nav menu entry.
export const externallyHandledPaths = [
  "/dashboards",
  "/customers",
  "/assets",
  "/ruleChains",
  "/devices",
  "/deviceProfiles",
  "/entityViews",
  "/widgets-bundles",
  "/auditLogs",
  "/usage",
];

const isExternallyHandledPath = (path: string) =>
  externallyHandledPaths.some((prefix) => path.startsWith(prefix));

/**
 * Initialises a window property for updating the router path.
 * Operation is idempotent.
 */
export const initExternalRouteSync = (dispatch: DispatchFn) => {
  if (routePropertyName in window) return false;

  /**
   * Handles a routing event from an external iframe.
   * If the event should result in a routing change then we
   * push to history and update the currentRoute store.
   *
   * @param path url path to show in the browser location bar. Needs /app prefix
   * @param pageTitle string to append in the page title
   */
  const handleExternalRouteChange: Window["handleRouteChange"] = (
    path,
    pageTitle
  ) => {
    const currentUrlPath = window.location.pathname;
    // remove query params, see PR-2453
    const [cleanPath] = path.split("?");
    const url = getUrlFromPath(cleanPath);

    if (isExternallyHandledPath(cleanPath)) {
      setTitle(pageTitle);
      if (currentUrlPath !== url) {
        window.history.pushState(null, "", url);
        dispatch(setCurrentRoute(cleanPath));
      }
    }
  };

  Object.defineProperty(window, routePropertyName, {
    value: handleExternalRouteChange,
    writable: false,
  });
  return true;
};

export const initExternalBreadcrumbSync = (dispatch: DispatchFn) => {
  if (breadcrumbPropertyName in window) return false;

  /**
   * Handles a breadcrumb change event from an external iframe.
   *
   * @param breadCrumbs array of breadcrumbs to display in app header
   */
  const handleExternalBreadcrumbChange: Window["handleBreadcrumbChange"] = (
    breadCrumbs: Breadcrumb[]
  ) => {
    dispatch(addBreadCrumbs(breadCrumbs));
  };

  Object.defineProperty(window, breadcrumbPropertyName, {
    value: handleExternalBreadcrumbChange,
    writable: false,
  });
  return true;
};
